import React, { useRef } from 'react';
import { BrandCarouselItem } from './brand-carousel-item';
import { BrandPageCarouselFieldsFragment } from '@/types';
import {
  FlatList,
  Platform,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { HorizontalListHeader } from './horizontal-list-header';
import { useLayoutContext } from '@/contexts/layout-context';

interface Props {
  containerStyle?: StyleProp<ViewStyle>;
  data: BrandPageCarouselFieldsFragment[];
  title: string;
}

const DESKTOP_CARD_WIDTH = 424;
const MOBILE_CARD_WIDTH = 172;

export const BrandsCarousel: React.FC<Props> = ({
  containerStyle,
  data,
  title,
}) => {
  const { isSmallLayout, appDimensions } = useLayoutContext();
  const listRef = useRef<FlatList>(null);
  const listContentWidthRef = useRef(0);
  const scrollOffsetRef = useRef(0);
  const itemWidth = isSmallLayout ? MOBILE_CARD_WIDTH : DESKTOP_CARD_WIDTH;
  const itemCount = data.length;
  const renderArrows =
    Platform.OS === 'web' && itemCount * itemWidth > appDimensions.width;

  return (
    <View style={[styles.base, containerStyle]}>
      <HorizontalListHeader
        itemCount={itemCount}
        largeHeaderStyle={styles.largeHeaderStyle}
        onLeftArrowPress={
          renderArrows
            ? () => {
                listRef.current?.scrollToOffset({
                  animated: true,
                  offset: Math.max(0, scrollOffsetRef.current - itemWidth),
                });
              }
            : undefined
        }
        onRightArrowPress={
          renderArrows
            ? () => {
                listRef.current?.scrollToOffset({
                  animated: true,
                  offset: Math.min(
                    listContentWidthRef.current,
                    scrollOffsetRef.current + itemWidth,
                  ),
                });
              }
            : undefined
        }
        title={title}
      />
      <FlatList
        data={data}
        horizontal
        onContentSizeChange={(width) => {
          listContentWidthRef.current = width;
        }}
        onScroll={(e) => {
          const offsetX = e.nativeEvent.contentOffset.x;
          scrollOffsetRef.current = offsetX;
        }}
        ref={listRef}
        renderItem={({ item }) => (
          <BrandCarouselItem
            brandPageUUID={item.uuid}
            description={item.carousel_module.description}
            desktopImageURL={item.carousel_module.desktop_image_url}
            logoImageURL={item.logo_image_url!}
            mobileImageURL={item.carousel_module.mobile_image_url}
            name={item.brand_name}
          />
        )}
        scrollEventThrottle={100}
        showsHorizontalScrollIndicator={false}
        style={styles.listLarge}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  base: {
    paddingTop: 8,
  },
  largeHeaderStyle: {
    marginBottom: 8,
    marginHorizontal: 16,
    paddingTop: 8,
  },
  listLarge: {
    paddingHorizontal: 8,
  },
});
